import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { Loader2, X } from "lucide-react";
import { addSalesRepCommission } from "../../store/slices/commissionAndRepaymentSlice";
import toast from "react-hot-toast";
import Select from "react-select";
import { useInfiniteSalesReps } from "../../hooks/useInfiniteSalesReps";
import { useInfiniteCustomers } from "../../hooks/useInfiniteCustomers";
import Button, { ButtonType } from "../Assets/Button";

interface AddCommissionModalProps {
  open: boolean;
  onClose: () => void;
}

const AddCommissionModal: React.FC<AddCommissionModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch: RootDispatch = useDispatch();

  const addingCommission = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.loadingState.addingRepaymentLoading,
  );

  const [selectedSalesRepId, setSelectedSalesRepId] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  const {
    salesReps: infiniteSalesReps,
    isLoadingMore: isLoadingMoreSalesReps,
    hasMore: hasMoreSalesReps,
    loadMore: loadMoreSalesReps,
    handleSearch: handleSalesRepSearch,
    reset: resetSalesReps,
  } = useInfiniteSalesReps(true);

  const {
    customers: infiniteCustomers,
    isLoadingMore: isLoadingMoreCustomers,
    hasMore: hasMoreCustomers,
    loadMore: loadMoreCustomers,
    handleSearch: handleCustomerSearch,
    reset: resetCustomers,
    handleSalesRepFilter,
    selectedSalesRepId: currentSalesRepFilter,
  } = useInfiniteCustomers(20);

  useEffect(() => {
    if (selectedSalesRepId) {
      handleSalesRepFilter(selectedSalesRepId);
    } else {
      handleSalesRepFilter("");
    }
  }, [selectedSalesRepId, handleSalesRepFilter]);

  const salesRepOptions = infiniteSalesReps.map((rep: any) => ({
    value: rep._id,
    label: rep.name,
  }));

  const customerOptions = infiniteCustomers.map((customer: any) => ({
    value: customer._id,
    label: customer.customerName,
  }));

  const SalesRepMenuList = (props: any) => {
    const { children } = props;
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        const bottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + 5;

        if (bottom && hasMoreSalesReps && !isLoadingMoreSalesReps) {
          loadMoreSalesReps();
        }
      },
      [hasMoreSalesReps, isLoadingMoreSalesReps, loadMoreSalesReps],
    );

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {children}
        {isLoadingMoreSalesReps && (
          <div className="flex justify-center items-center p-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>
    );
  };

  const CustomerMenuList = (props: any) => {
    const { children } = props;
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.target as HTMLDivElement;
        const bottom =
          target.scrollHeight - target.scrollTop <= target.clientHeight + 5;

        if (bottom && hasMoreCustomers && !isLoadingMoreCustomers) {
          loadMoreCustomers();
        }
      },
      [hasMoreCustomers, isLoadingMoreCustomers, loadMoreCustomers],
    );

    return (
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {children}
        {isLoadingMoreCustomers && (
          <div className="flex justify-center items-center p-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Loading more...</span>
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedSalesRepId) {
      toast.error("Please select a sales representative");
      return;
    }
    if (!selectedCustomerId) {
      toast.error("Please select a customer");
      return;
    }

    try {
      await dispatch(
        addSalesRepCommission({
          salesRepId: selectedSalesRepId,
          customerId: selectedCustomerId,
        }),
      ).unwrap();

      handleClose();
    } catch (error: any) {
      toast.error(error || "Failed to add commission");
    }
  };

  const handleClose = () => {
    setSelectedSalesRepId("");
    setSelectedCustomerId("");
    resetSalesReps();
    resetCustomers();
    onClose();
  };

  const selectedSalesRepValue = salesRepOptions.find(
    (opt) => opt.value === selectedSalesRepId,
  );

  const selectedCustomerValue = customerOptions.find(
    (opt) => opt.value === selectedCustomerId,
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-auto flex flex-col">
        <div className="bg-gray-100 px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Add New Commission
            </h2>
            <p className="text-gray-500 text-sm">
              Create a new commission entry for sales representative
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={addingCommission}
            className="text-gray-600 hover:text-gray-900 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 p-6">
          <div className="space-y-5">
            <div>
              <label className="block font-medium text-gray-700 mb-2 text-sm">
                Sales Representative *
              </label>
              <Select
                options={salesRepOptions}
                value={selectedSalesRepValue}
                onChange={(selectedOption: any) => {
                  setSelectedSalesRepId(selectedOption?.value || "");
                }}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    handleSalesRepSearch(inputValue);
                  }
                }}
                isClearable
                isDisabled={addingCommission}
                placeholder="Search and select sales rep..."
                components={{ MenuList: SalesRepMenuList }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base: any) => ({
                    ...base,
                    borderColor: "#e5e7eb",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#d1d5db",
                    },
                  }),
                  menu: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuPortal: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-2 text-sm">
                Customer *
              </label>
              <Select
                options={customerOptions}
                value={selectedCustomerValue}
                onChange={(selectedOption: any) => {
                  setSelectedCustomerId(selectedOption?.value || "");
                }}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === "input-change") {
                    handleCustomerSearch(inputValue);
                  }
                }}
                isClearable
                isDisabled={addingCommission || !selectedSalesRepId}
                placeholder="Search and select customer..."
                components={{ MenuList: CustomerMenuList }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: "40px",
                    borderColor: "#e5e7eb",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "#d1d5db",
                    },
                  }),
                  menu: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  menuPortal: (base: any) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </div>

          <div className="flex items-end gap-3 mt-auto pt-6 border-t">
            <Button
              type={ButtonType.Cancel}
              onClick={handleClose}
              className="flex-1 text-xs"
              disabled={addingCommission}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.Submit}
              disabled={addingCommission}
              onClick={handleSubmit}
              className="flex-1 text-xs"
            >
              {addingCommission ? (
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              ) : null}
              Add Commission
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCommissionModal;
